import { useState } from 'react';
import { NOMS_DES_JOURS, SEMAINE_AFFICHEE } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { styleChamp } from './champs';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import type { Routine } from '../../api/types';

/**
 * Poser ou corriger une routine — A LA MAIN, sans le moindre appel d'IA.
 *
 * Le meme formulaire sert aux deux : la seule difference est qu'une correction n'envoie
 * QUE ce qui a change, comme partout ailleurs dans Colette.
 */
export function FormulaireRoutine({
  routine,
  jourInitial,
  onFait,
  onAnnuler,
  signaler,
}: {
  /** Present = correction ; absent = creation. */
  routine?: Routine;
  /** Le jour du bouton « + Ajouter », pre-coche a la creation. */
  jourInitial?: number;
  onFait: (message: string) => void;
  onAnnuler: () => void;
  signaler: (message: string) => void;
}) {
  const [titre, setTitre] = useState(routine?.titre ?? '');
  const [debut, setDebut] = useState(routine?.debut ?? '');
  const [fin, setFin] = useState(routine?.fin ?? '');
  const [jours, setJours] = useState<number[]>(
    routine?.jours ?? (jourInitial === undefined ? [] : [jourInitial]),
  );
  const [envoi, setEnvoi] = useState(false);

  const basculerJour = (jour: number) =>
    setJours((actuels) =>
      actuels.includes(jour) ? actuels.filter((j) => j !== jour) : [...actuels, jour].sort(),
    );

  const complet = titre.trim() !== '' && debut !== '' && jours.length > 0;
  const finApresDebut = fin === '' || fin > debut;
  const peutEnvoyer = complet && finApresDebut && !envoi;

  const enregistrer = async () => {
    if (!peutEnvoyer) return;
    setEnvoi(true);
    try {
      if (routine === undefined) {
        await api.creerRoutine({
          title: titre.trim(),
          startTime: debut,
          endTime: fin === '' ? null : fin,
          days: jours,
        });
        onFait('Routine ajoutée');
      } else {
        // N'envoyer QUE ce qui change : un envoi complet ecraserait ce que l'ecran
        // n'affiche pas (meme regle que pour un evenement).
        const modification: Record<string, unknown> = {};
        if (titre.trim() !== routine.titre) modification.title = titre.trim();
        if (debut !== routine.debut) modification.startTime = debut;
        const finVoulue = fin === '' ? null : fin;
        if (finVoulue !== routine.fin) modification.endTime = finVoulue;
        if (jours.join(',') !== routine.jours.join(',')) modification.days = jours;
        if (Object.keys(modification).length === 0) {
          onAnnuler();
          return;
        }
        await api.modifierRoutine(routine.id, modification);
        onFait('Routine modifiée');
      }
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
      style={{
        background: C.carte,
        border: `1px solid ${C.bordureForte}`,
        borderRadius: 18,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <Champ libelle={routine === undefined ? 'Nouvelle routine' : 'Nom'}>
        <input
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          aria-label="Nom de la routine"
          placeholder="Dîner, escalade, dépôt des enfants…"
          autoFocus
          style={styleChamp}
        />
      </Champ>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Champ libelle="Début">
            <input
              type="time"
              value={debut}
              onChange={(e) => setDebut(e.target.value)}
              aria-label="Heure de début"
              style={styleChamp}
            />
          </Champ>
        </div>
        <div style={{ flex: 1 }}>
          <Champ libelle="Fin (facultatif)">
            <input
              type="time"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              aria-label="Heure de fin"
              style={styleChamp}
            />
          </Champ>
        </div>
      </div>
      {!finApresDebut ? (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.corailTexte }}>
          La fin doit venir après le début.
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, color: C.tertiaire }}>
          QUELS JOURS ?
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {SEMAINE_AFFICHEE.map((jour) => {
            const actif = jours.includes(jour);
            return (
              <button
                key={jour}
                type="button"
                role="checkbox"
                aria-checked={actif}
                aria-label={NOMS_DES_JOURS[jour]}
                onClick={() => basculerJour(jour)}
                style={{
                  flex: 1,
                  height: 38,
                  border: `1px solid ${actif ? 'transparent' : C.bordure}`,
                  borderRadius: 10,
                  background: actif ? DEGRADE_ACTION : 'transparent',
                  color: actif ? '#FFFFFF' : C.secondaire,
                  fontSize: 12,
                  fontWeight: 800,
                  fontFamily: 'inherit',
                  textTransform: 'capitalize',
                  cursor: 'pointer',
                }}
              >
                {NOMS_DES_JOURS[jour].slice(0, 1).toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          className="pressable"
          disabled={!peutEnvoyer}
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 12,
            background: peutEnvoyer ? DEGRADE_ACTION : C.inactif,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: peutEnvoyer ? 'pointer' : 'default',
          }}
        >
          {envoi ? '…' : 'Enregistrer'}
        </button>
        <button
          type="button"
          className="pressable"
          onClick={onAnnuler}
          style={{
            height: 44,
            padding: '0 14px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 12,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

function Champ({ libelle, children }: { libelle: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, color: C.tertiaire }}>
        {libelle.toUpperCase()}
      </span>
      {children}
    </label>
  );
}
